import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, Row, Col, Card, Spinner, Badge } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { downloadQuotePDF, printQuotePDF, getQuotePDFBase64 } from '../utils/pdfGenerator';
import { sendQuoteViaWhatsApp, normalizeWhatsAppPhone } from '../utils/whatsappHelper';
import { sendDocumentEmail, isValidEmail } from '../utils/emailHelper';

const QuoteSuccessModal = ({
    show,
    onHide,
    quote,
    user,
    sellers = [],
    onDone
}) => {
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSentSuccess, setEmailSentSuccess] = useState(false);
    const [whatsappSentSuccess, setWhatsappSentSuccess] = useState(false);

    useEffect(() => {
        if (show && quote) {
            let rawPhone = quote.cliente_d_whatsapp || quote.d_whatsapp || quote.n_celul || quote.n_telef || '';
            if (rawPhone && typeof rawPhone === 'string') {
                if (rawPhone.includes('/')) {
                    rawPhone = rawPhone.split('/')[0].trim();
                } else if (rawPhone.includes(',')) {
                    rawPhone = rawPhone.split(',')[0].trim();
                }
            }
            setWhatsappNumber(rawPhone);

            const rawEmail = quote.cliente_d_email || quote.d_email || '';
            setEmailAddress(rawEmail);

            setIsSendingEmail(false);
            setEmailSentSuccess(false);
            setWhatsappSentSuccess(false);
        }
    }, [show, quote]);

    if (!quote) return null;

    const numPresuStr = quote.n_presu 
        ? String(quote.n_presu).padStart(8, '0') 
        : String(quote.id || '0').padStart(8, '0');
    const docLabel = `${quote.t_presu || 'PRE'} ${numPresuStr}`;
    const clientName = quote.d_razon ? quote.d_razon.trim() : 'Cliente General';
    const totalAmount = parseFloat(quote.i_total) || 0;

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(val);
    };

    const handleSendWhatsApp = () => {
        const trimmed = whatsappNumber.trim();
        if (!trimmed) {
            Swal.fire({
                title: 'WhatsApp Requerido',
                text: 'Por favor ingrese el número de celular / WhatsApp del cliente.',
                icon: 'warning',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        const normalized = normalizeWhatsAppPhone(trimmed);
        if (normalized.length < 10) {
            Swal.fire({
                title: 'Número Inválido',
                text: 'El número ingresado parece incompleto. Verifique código de área y número.',
                icon: 'warning',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        const quoteWithPhone = {
            ...quote,
            d_whatsapp: trimmed,
            cliente_d_whatsapp: trimmed,
            n_celul: trimmed
        };

        sendQuoteViaWhatsApp({
            quote: quoteWithPhone,
            user,
            sellers
        });
        setWhatsappSentSuccess(true);
    };

    const handleSendEmail = async () => {
        const trimmed = emailAddress.trim();
        if (!trimmed) {
            Swal.fire({
                title: 'Email Requerido',
                text: 'Por favor ingrese el correo electrónico del cliente.',
                icon: 'warning',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        if (!isValidEmail(trimmed)) {
            Swal.fire({
                title: 'Email Inválido',
                text: 'Por favor ingrese una dirección de correo electrónico válida (ej: cliente@correo.com).',
                icon: 'warning',
                confirmButtonColor: '#3085d6'
            });
            return;
        }

        setIsSendingEmail(true);
        try {
            const quoteId = quote.id || quote.n_presu;
            const companyId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id || 2;
            const empresaNombre = user?.empresa_activa?.nombre || 'SISGECOM';
            const pdfBase64 = getQuotePDFBase64(quote, user, sellers);

            const res = await sendDocumentEmail({
                url: `http://127.0.0.1:8000/api/presupuestos/${quoteId}/enviar-email/`,
                email: trimmed,
                asunto: `Presupuesto Nº ${docLabel} - ${empresaNombre}`,
                mensaje: `Estimado/a ${clientName},\n\nLe acercamos adjunto el presupuesto solicitado.\n\nSaludos cordiales,\n${empresaNombre}`,
                pdfBase64: pdfBase64,
                companyId: companyId,
                successMessage: `Presupuesto Nº ${docLabel} enviado exitosamente a ${trimmed}.`
            });

            if (res.success) {
                setEmailSentSuccess(true);
            }
        } catch (err) {
            console.error("Error al enviar email:", err);
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handlePrint = () => {
        try {
            printQuotePDF(quote, user, sellers);
        } catch (err) {
            Swal.fire({
                title: 'Error al Imprimir',
                text: 'No se pudo generar la vista de impresión.',
                icon: 'error',
                confirmButtonColor: '#3085d6'
            });
        }
    };

    const handleDownload = () => {
        try {
            downloadQuotePDF(quote, user, sellers);
        } catch (err) {
            Swal.fire({
                title: 'Error al Descargar',
                text: 'No se pudo generar el archivo PDF.',
                icon: 'error',
                confirmButtonColor: '#3085d6'
            });
        }
    };

    const handleClose = () => {
        if (typeof onDone === 'function') {
            onDone();
        } else if (typeof onHide === 'function') {
            onHide();
        }
    };

    return (
        <Modal 
            show={show} 
            onHide={handleClose} 
            backdrop="static" 
            keyboard={false} 
            centered 
            size="lg"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="d-flex align-items-center gap-2">
                    <span className="badge bg-success rounded-circle p-2 d-inline-flex align-items-center justify-content-center" style={{ width: '34px', height: '34px' }}>
                        <i className="bi bi-check-lg text-white fs-5"></i>
                    </span>
                    <div>
                        <h5 className="mb-0 fw-bold text-dark">¡Presupuesto Guardado con Éxito!</h5>
                        <small className="text-muted">El comprobante ha sido registrado correctamente en el sistema</small>
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="pt-3 pb-2">
                {/* Resumen del Comprobante */}
                <Card className="bg-light border-0 mb-3 shadow-sm">
                    <Card.Body className="py-2 px-3">
                        <Row className="align-items-center gy-2">
                            <Col sm={4}>
                                <span className="text-muted small d-block" style={{ fontSize: '0.8rem' }}>Número de Presupuesto</span>
                                <span className="fw-bold fs-6 text-primary">{docLabel}</span>
                            </Col>
                            <Col sm={5}>
                                <span className="text-muted small d-block" style={{ fontSize: '0.8rem' }}>Cliente</span>
                                <span className="fw-bold text-dark text-truncate d-block" style={{ fontSize: '0.9rem' }} title={clientName}>
                                    {clientName}
                                </span>
                            </Col>
                            <Col sm={3} className="text-sm-end">
                                <span className="text-muted small d-block" style={{ fontSize: '0.8rem' }}>Importe Total</span>
                                <span className="fw-bold fs-6 text-success">{formatCurrency(totalAmount)}</span>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <div className="d-flex align-items-center mb-2 px-1">
                    <i className="bi bi-share-fill me-2 text-primary" style={{ fontSize: '0.85rem' }}></i>
                    <span className="fw-semibold text-secondary small" style={{ fontSize: '0.82rem' }}>
                        Enviar comprobante o imprimir:
                    </span>
                </div>

                <Row className="g-2 mb-3">
                    {/* Opción WhatsApp */}
                    <Col md={6}>
                        <Card className="h-100 border shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-1" style={{ minHeight: '24px' }}>
                                        <span className="fw-bold text-dark small d-flex align-items-center gap-1">
                                            <i className="bi bi-whatsapp text-success"></i>
                                            WhatsApp
                                        </span>
                                        {whatsappSentSuccess ? (
                                            <Badge bg="success-subtle" className="text-success border border-success" style={{ fontSize: '0.7rem' }}>
                                                <i className="bi bi-check2 me-1"></i> Abierto
                                            </Badge>
                                        ) : <span style={{ height: '18px' }}></span>}
                                    </div>
                                    <p className="text-muted mb-2" style={{ fontSize: '0.78rem', lineHeight: '1.25', minHeight: '32px' }}>
                                        Envía el presupuesto detallado en texto y descarga el PDF adjunto.
                                    </p>
                                    <Form.Group className="mb-2">
                                        <InputGroup size="sm">
                                            <InputGroup.Text className="bg-light py-1 px-2" style={{ borderRadius: '6px 0 0 6px' }}>
                                                <i className="bi bi-telephone text-muted" style={{ fontSize: '0.85rem' }}></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="text"
                                                placeholder="Ej: 11 4567 8901"
                                                value={whatsappNumber}
                                                onChange={(e) => setWhatsappNumber(e.target.value)}
                                                style={{ fontSize: '0.85rem', borderRadius: '0 6px 6px 0' }}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </div>
                                <Button 
                                    variant="success" 
                                    size="sm" 
                                    className="w-100 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-1"
                                    onClick={handleSendWhatsApp}
                                    style={{ backgroundColor: '#25D366', borderColor: '#25D366', height: '34px', fontSize: '0.85rem', borderRadius: '6px' }}
                                >
                                    <i className="bi bi-whatsapp"></i>
                                    Enviar por WhatsApp
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Opción Email */}
                    <Col md={6}>
                        <Card className="h-100 border shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-1" style={{ minHeight: '24px' }}>
                                        <span className="fw-bold text-dark small d-flex align-items-center gap-1">
                                            <i className="bi bi-envelope-at-fill text-primary"></i>
                                            Correo Electrónico
                                        </span>
                                        {emailSentSuccess ? (
                                            <Badge bg="success-subtle" className="text-success border border-success" style={{ fontSize: '0.7rem' }}>
                                                <i className="bi bi-check2 me-1"></i> Enviado
                                            </Badge>
                                        ) : <span style={{ height: '18px' }}></span>}
                                    </div>
                                    <p className="text-muted mb-2" style={{ fontSize: '0.78rem', lineHeight: '1.25', minHeight: '32px' }}>
                                        Envía el presupuesto oficial con plantilla HTML y PDF adjunto.
                                    </p>
                                    <Form.Group className="mb-2">
                                        <InputGroup size="sm">
                                            <InputGroup.Text className="bg-light py-1 px-2" style={{ borderRadius: '6px 0 0 6px' }}>
                                                <i className="bi bi-envelope text-muted" style={{ fontSize: '0.85rem' }}></i>
                                            </InputGroup.Text>
                                            <Form.Control
                                                type="email"
                                                placeholder="ejemplo@cliente.com"
                                                value={emailAddress}
                                                onChange={(e) => setEmailAddress(e.target.value)}
                                                disabled={isSendingEmail}
                                                style={{ fontSize: '0.85rem', borderRadius: '0 6px 6px 0' }}
                                            />
                                        </InputGroup>
                                    </Form.Group>
                                </div>
                                <Button 
                                    variant="primary" 
                                    size="sm" 
                                    className="w-100 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-1"
                                    onClick={handleSendEmail}
                                    disabled={isSendingEmail}
                                    style={{ height: '34px', fontSize: '0.85rem', borderRadius: '6px' }}
                                >
                                    {isSendingEmail ? (
                                        <>
                                            <Spinner animation="border" size="sm" />
                                            <span className="ms-1">Enviando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-send-fill"></i>
                                            Enviar por Email
                                        </>
                                    )}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Opciones Rápidas: Imprimir y Descargar PDF */}
                <Row className="g-2 mb-2">
                    <Col xs={6}>
                        <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            className="w-100 fw-semibold d-flex align-items-center justify-content-center gap-1 text-dark"
                            onClick={handlePrint}
                            style={{ height: '34px', fontSize: '0.85rem', borderRadius: '6px' }}
                        >
                            <i className="bi bi-printer text-dark"></i>
                            Imprimir Presupuesto
                        </Button>
                    </Col>
                    <Col xs={6}>
                        <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            className="w-100 fw-semibold d-flex align-items-center justify-content-center gap-1 text-danger"
                            onClick={handleDownload}
                            style={{ height: '34px', fontSize: '0.85rem', borderRadius: '6px' }}
                        >
                            <i className="bi bi-file-earmark-pdf-fill text-danger"></i>
                            Descargar PDF
                        </Button>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0 pb-3">
                <Button 
                    variant="primary" 
                    className="w-100 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2"
                    onClick={handleClose}
                    style={{ height: '38px', fontSize: '0.92rem', borderRadius: '6px' }}
                >
                    <i className="bi bi-plus-circle"></i>
                    Aceptar y Crear Nuevo Presupuesto
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default QuoteSuccessModal;
